import {
  Control,
  GetControlRequest,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { ModelStatsServicePromiseClient } from '@trustero/trustero-api-web/lib/model/stats_grpc_web_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { useInAudit } from 'src/context/AuditContext'
import { GrpcResponse, NewGrpcResponse } from '../../hooks/types'
import { useNewModel } from '../../hooks/useNewModel'
import { useSwrImmutableGrpc } from '../../useSwrImmutableGrpc'
import { useControls, useInvalidateControlsCache } from './useControls'

export const mutateDependencies = async (
  methodMutator: (asyncCall: unknown) => Promise<unknown>,
): Promise<void> => {
  await Promise.all([
    methodMutator(
      ModelStatsServicePromiseClient.prototype.listControlComplianceStats,
    ),
    methodMutator(ModelPromiseClient.prototype.getControlStats),
    methodMutator(ModelPromiseClient.prototype.listControlIds),
    methodMutator(ModelStatsServicePromiseClient.prototype.getTrusteroScore),
  ])
}

/**
 * @param controlModelId modelId
 * Can take control id (PK) or model id
 * Will only have instant lookup in the cache if model id is passed in
 * This is meant to always respect the current audit
 */
export const useControl = (
  controlModelId?: string,
  shouldFetch = true,
): GrpcResponse<Control> => {
  const { auditId } = useInAudit()
  const request = new ListControlsRequest()
  if (auditId) {
    request.setAuditId(new StringValue().setValue(auditId))
  }

  const response = useControls(request, !!controlModelId && shouldFetch, {
    ignoreAuditContext: true,
  })
  const mutate = useInvalidateControlsCache()

  // Find the specific control from the controls list
  const control = response.data
    ?.getItemsList()
    .find(
      (c) =>
        c.getId() === controlModelId ||
        c.getModelId() === controlModelId ||
        c.getMigratedControlId() === controlModelId,
    )

  return NewGrpcResponse({
    ...response,
    data: control,
    mutate,
  })
}

/**
 * @param controlId
 * Can take control id (PK) or model id
 */
export const useControlModel = (
  controlId: string,
  shouldFetch = true,
): GrpcResponse<Control> => {
  const newModel = useNewModel()
  const request = new GetControlRequest().setModelId(controlId)
  if (newModel) {
    request.setUseNewModel(true)
  }

  const { response } = useSwrImmutableGrpc(
    ModelPromiseClient.prototype.getControl,
    request,
    shouldFetch,
  )

  return NewGrpcResponse(response)
}

/**
 * @param controlModelId modelId
 * Can take control id (PK) or model id
 * Will only have instant lookup in the cache if model id is passed in
 */
export const useControlIgnoreAudit = (
  controlModelId?: string,
  shouldFetch = true,
): GrpcResponse<Control> => {
  const request = new ListControlsRequest()

  const response = useControls(
    request,
    !!controlModelId && shouldFetch,
    {
      ignoreAuditContext: true,
    },
    undefined,
    undefined,
    true,
  )
  const controls = response.data

  const control = controls
    ?.getItemsList()
    .find(
      (c) => c.getId() === controlModelId || c.getModelId() === controlModelId,
    )

  return NewGrpcResponse({
    ...response,
    data: control,
  })
}
